/**
 * ConversationThread — the chat UI for a single conversation.
 *
 * Composition:
 *   <Header />        student name + email + online dot
 *   <MessageList />   scrollable virtualized-ish list of bubbles
 *   <TypingIndicator />
 *   <Composer />      textarea + send button
 *
 * Scroll behaviour:
 *   We track whether the user is "pinned to bottom" (within 80px of the
 *   latest message). When new messages arrive, we only auto-scroll if
 *   they were pinned — otherwise we preserve their reading position and
 *   show a small "new messages" affordance is *not* implemented in this
 *   pass (see TODO). Scrolling near the top triggers loading the next
 *   (older) page via the infinite query.
 *
 * Accessibility:
 *   - `role="log"` + `aria-live="polite"` on the message list so screen
 *     readers announce new messages without stealing focus.
 *   - Composer is a labelled textarea; Send button is reachable by Tab.
 *   - Enter sends, Shift+Enter inserts a newline.
 */

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { formatDistanceToNow, format } from 'date-fns';
import {
  useConversation,
  useEmitTyping,
  useMarkAsRead,
  useMessages,
  useSendMessage,
  useTypingIndicator,
  type OptimisticMessage,
} from '../hooks/useMessages';
import { wsService } from '../services/websocket';
import { useToast } from './Toast';
import type { Message } from '../services/messagesApi';
import styles from './ConversationThread.module.css';

interface Props {
  conversationId: string;
  onClose?: () => void; // mobile back button
  className?: string;
}

const MAX_LENGTH = 4000;

const ConversationThread: React.FC<Props> = ({ conversationId, onClose, className }) => {
  const toast = useToast();
  const { data: conversation } = useConversation(conversationId);
  const {
    data,
    isLoading,
    isError,
    refetch,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useMessages(conversationId);
  const sendMessage = useSendMessage();
  const markAsRead = useMarkAsRead();
  const { isTyping, typingName } = useTypingIndicator(conversationId);
  const emitTyping = useEmitTyping(conversationId);

  const [draft, setDraft] = useState('');
  const [isOnline, setIsOnline] = useState<boolean>(() => wsService.isConnected());

  const scrollRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const pinnedToBottomRef = useRef(true);
  const prevScrollHeightRef = useRef(0);
  const prevNewestIdRef = useRef<string | null>(null);

  // ---------- derive a flat oldest→newest message list ----------
  const messages = useMemo<OptimisticMessage[]>(() => {
    if (!data) return [];
    // pages are arrays in newest-first order; reverse each page and reverse
    // the page order so the final array is oldest → newest.
    const flat: Message[] = [];
    for (let i = data.pages.length - 1; i >= 0; i--) {
      const page = data.pages[i].messages;
      for (let j = page.length - 1; j >= 0; j--) {
        flat.push(page[j]);
      }
    }
    return flat as OptimisticMessage[];
  }, [data]);

  // ---------- scroll preservation ----------
  // Before render: capture scrollHeight so we can restore offset after older
  // messages prepend at the top.
  useLayoutEffect(() => {
    if (scrollRef.current) {
      prevScrollHeightRef.current = scrollRef.current.scrollHeight;
    }
  });

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || messages.length === 0) return;
    const newestId = messages[messages.length - 1].id;
    const newestChanged = newestId !== prevNewestIdRef.current;

    if (pinnedToBottomRef.current && newestChanged) {
      el.scrollTop = el.scrollHeight;
    } else if (!newestChanged) {
      // We just prepended older pages — keep the user's eyeline anchored.
      const delta = el.scrollHeight - prevScrollHeightRef.current;
      if (delta > 0) {
        el.scrollTop += delta;
      }
    }
    prevNewestIdRef.current = newestId;
  }, [messages]);

  // ---------- scroll handler ----------
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      const distanceFromBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight;
      pinnedToBottomRef.current = distanceFromBottom < 80;

      if (el.scrollTop < 120 && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  // ---------- mark-as-read on view ----------
  useEffect(() => {
    if (!conversationId || messages.length === 0) return;
    const unread = messages
      .filter((m) => m.senderRole === 'student' && !m.readAt && !m.clientId)
      .map((m) => m.id);
    if (unread.length === 0) return;
    // Defer so we batch across re-renders.
    const timer = setTimeout(() => {
      markAsRead.mutate({ conversationId, messageIds: unread });
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, messages.length]);

  // ---------- WS connection presence ----------
  useEffect(() => {
    const update = () => setIsOnline(wsService.isConnected());
    const interval = setInterval(update, 5000);
    return () => clearInterval(interval);
  }, []);

  // ---------- send handler ----------
  const handleSend = useCallback(
    async (retryArgs?: { body: string; clientId: string }) => {
      const body = (retryArgs?.body ?? draft).trim();
      if (!body || body.length > MAX_LENGTH || sendMessage.isPending) return;
      try {
        await sendMessage.mutateAsync({
          conversationId,
          body,
          clientId: retryArgs?.clientId,
        });
        if (!retryArgs) {
          setDraft('');
          pinnedToBottomRef.current = true;
          composerRef.current?.focus();
        }
      } catch (err) {
        toast.showError('Failed to send message. Tap retry to try again.');
      }
    },
    [conversationId, draft, sendMessage, toast]
  );

  const handleRetry = useCallback(
    (msg: OptimisticMessage) => {
      if (!msg.clientId) return;
      void handleSend({ body: msg.body, clientId: msg.clientId });
    },
    [handleSend]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void handleSend();
      } else {
        emitTyping();
      }
    },
    [handleSend, emitTyping]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setDraft(e.target.value);
      // Auto-grow up to a max height handled in CSS.
      const ta = e.target;
      ta.style.height = 'auto';
      ta.style.height = `${Math.min(ta.scrollHeight, 180)}px`;
    },
    []
  );

  // ---------- render ----------
  return (
    <section className={`${styles.root} ${className ?? ''}`} aria-label="Conversation">
      <header className={styles.header}>
        {onClose && (
          <button
            type="button"
            className={styles.backBtn}
            onClick={onClose}
            aria-label="Back to conversations"
          >
            ←
          </button>
        )}
        <div className={styles.headerInfo}>
          <h2 className={styles.headerName}>
            {conversation?.studentName ?? 'Loading…'}
            <span
              className={`${styles.presenceDot} ${isOnline ? styles.online : styles.offline}`}
              aria-label={isOnline ? 'Connected' : 'Disconnected'}
              title={isOnline ? 'Online' : 'Offline'}
            />
          </h2>
          <p className={styles.headerEmail}>{conversation?.studentEmail}</p>
        </div>
      </header>

      <div
        ref={scrollRef}
        className={styles.messageList}
        role="log"
        aria-live="polite"
        aria-relevant="additions"
        onScroll={handleScroll}
      >
        {isFetchingNextPage && (
          <div className={styles.loadingBar}>Loading older messages…</div>
        )}
        {isLoading ? (
          <div className={styles.empty}>Loading conversation…</div>
        ) : isError ? <div className={styles.empty} role="alert"><p>Couldn&apos;t load this conversation.</p><button type="button" onClick={() => refetch()}>Retry</button></div> : messages.length === 0 ? (
          <div className={styles.empty}>No messages yet. Say hello.</div>
        ) : (
          messages.map((m, idx) => {
            const prev = messages[idx - 1];
            const showDay =
              !prev ||
              new Date(prev.sentAt).toDateString() !==
                new Date(m.sentAt).toDateString();
            return (
              <React.Fragment key={m.id}>
                {showDay && (
                  <div className={styles.daySeparator} aria-hidden="true">
                    <span>{format(new Date(m.sentAt), 'EEE, MMM d')}</span>
                  </div>
                )}
                <MessageBubble
                  message={m}
                  isOwn={m.senderRole === 'instructor'}
                  onRetry={handleRetry}
                />
              </React.Fragment>
            );
          })
        )}
      </div>

      {isTyping && (
        <div className={styles.typingRow} aria-live="polite">
          <span className={styles.typingDots}>
            <span /><span /><span />
          </span>
          <span>{typingName ?? 'Student'} is typing…</span>
        </div>
      )}

      <form
        className={styles.composer}
        onSubmit={(e) => {
          e.preventDefault();
          void handleSend();
        }}
      >
        <label htmlFor="message-composer" className={styles.srOnly}>
          Message
        </label>
        <textarea
          id="message-composer"
          ref={composerRef}
          className={styles.composerInput}
          value={draft}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder="Type a message — Enter to send, Shift+Enter for newline"
          rows={1}
          maxLength={MAX_LENGTH}
          aria-label="Message to student"
        />
        <div className={styles.composerFooter}>
          <span className={styles.charCounter} aria-live="off">
            {draft.length} / {MAX_LENGTH}
          </span>
          <button
            type="submit"
            className={styles.sendBtn}
            disabled={!draft.trim() || sendMessage.isPending || draft.length > MAX_LENGTH}
            aria-label="Send message"
          >
            {sendMessage.isPending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </form>
    </section>
  );
};

// =====================================================
// MESSAGE BUBBLE
// =====================================================

interface BubbleProps {
  message: OptimisticMessage;
  isOwn: boolean;
  onRetry: (m: OptimisticMessage) => void;
}

const MessageBubble: React.FC<BubbleProps> = ({ message, isOwn, onRetry }) => {
  if (message.senderRole === 'system') {
    return (
      <div className={styles.systemRow} role="status">
        <span className={styles.systemBubble}>{message.body}</span>
      </div>
    );
  }

  const sentAtLabel = formatDistanceToNow(new Date(message.sentAt), {
    addSuffix: true,
  });
  const isFailed = message.clientStatus === 'failed';
  const isSending = message.clientStatus === 'sending';

  return (
    <div
      className={`${styles.bubbleRow} ${isOwn ? styles.bubbleRowOwn : styles.bubbleRowOther}`}
    >
      <div
        className={`${styles.bubble} ${isOwn ? styles.bubbleOwn : styles.bubbleOther} ${
          isFailed ? styles.bubbleFailed : ''
        }`}
      >
        {!isOwn && <div className={styles.senderName}>{message.senderName}</div>}
        <div className={styles.bubbleBody}>{message.body}</div>
        <div className={styles.bubbleMeta}>
          <time
            dateTime={message.sentAt}
            title={format(new Date(message.sentAt), 'PPpp')}
          >
            {sentAtLabel}
          </time>
          {message.edited && <span className={styles.editedTag}>(edited)</span>}
          {isOwn && (
            <span className={styles.receipt} aria-label={message.readAt ? 'Read' : 'Sent'}>
              {isSending ? '…' : isFailed ? '!' : message.readAt ? '✓✓' : '✓'}
            </span>
          )}
        </div>
        {isFailed && (
          <button
            type="button"
            className={styles.retryBtn}
            onClick={() => onRetry(message)}
          >
            Failed — retry
          </button>
        )}
      </div>
    </div>
  );
};

export default ConversationThread;
